import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type RetentionState = 'not_collected' | 'active' | 'scheduled' | 'due' | 'disposed';

export interface MyNumberLifecycle {
  employeeId: string;
  name: string;
  employeeCode: string | null;
  status: string;
  collected: boolean;
  last4: string | null;
  purpose: string | null;
  collectedAt: string | null;
  terminationDate: string | null;
  disposeAfter: string | null;
  disposedAt: string | null;
  state: RetentionState;
}

export interface MyNumberLifecycleResponse {
  retentionYears: number;
  employees: MyNumberLifecycle[];
  summary: Record<RetentionState, number>;
}

export function useMyNumberLifecycle() {
  return useQuery({
    queryKey: ['my-number', 'lifecycle'],
    queryFn: async () => {
      const r = await api.get<MyNumberLifecycleResponse>('/api/my-number/lifecycle');
      return r.data;
    },
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['my-number'] });
  qc.invalidateQueries({ queryKey: ['employees'] });
}

export function useCollectMyNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employeeId: string; plaintext: string; purpose: string }) => {
      const r = await api.post<MyNumberLifecycle>(`/api/my-number/${input.employeeId}/collect`, {
        plaintext: input.plaintext,
        purpose: input.purpose,
      });
      return r.data;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useDisposeMyNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { employeeId: string; reason: string }) => {
      const r = await api.post<MyNumberLifecycle>(`/api/my-number/${input.employeeId}/dispose`, { reason: input.reason });
      return r.data;
    },
    onSuccess: () => invalidate(qc),
  });
}

export function useRunMyNumberSweep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<{ disposed: number }>('/api/my-number/run-disposal-sweep', {});
      return r.data;
    },
    onSuccess: () => invalidate(qc),
  });
}
